/**
 * Phase 7.2 — Part X of Y UI (computed-first label).
 *
 * Doctrine:
 *   - Bucket 4 §4.x   Split UI must not assume binary splits. A split
 *                     group may contain 2, 3, 4+ parts.
 *   - Bucket 4 §4.x   Rollover / continuation tickets are NOT job-line
 *                     parts and must not be labeled "Part X of Y".
 *
 * Why this rewrite
 * ----------------
 * Pre-7.2 the badge label was a hardcoded `Part ${splitSequence} of 2`
 * which silently misrepresented 3+ part splits as binary. Phase 7.1
 * landed the canonical `getSplitPartLabelData` helper. This slice
 * swaps the hardcode for the helper's computed label and preserves a
 * safe fallback ("Split") when group identity is unavailable.
 *
 * Caller adoption shape (smallest safe):
 *   - Existing callers that hold only a single `ticket` keep passing
 *     `splitParentId / splitGroupId / splitSequence / rolloverOfTicketId`.
 *     The badge falls back to a generic "Split" label when group
 *     identity is not supplied — never "of 2".
 *   - Callers that can reconstruct group identity upstream (Phase 7.3
 *     adoption point — e.g., the board hook that holds all tickets)
 *     pass `splitGroup` and the badge formats "Part X of Y" via
 *     `getSplitPartLabelData`.
 *   - Callers that already have a pre-formatted label (rare; tests,
 *     ad-hoc UIs) can pass `splitLabelData` directly.
 *
 * Rollover continuation handling
 *   - The existing `RolloverBadge` (rendered separately in
 *     `TicketCard.tsx`) already conveys rollover lineage. To avoid
 *     dual-labeling the same row as both "Rollover" and "Part X of Y",
 *     this component returns `null` when `rolloverOfTicketId` is set.
 *     Phase 7.3 may introduce an explicit "Continuation" badge if
 *     deeper UI signalling is needed.
 */
import { Badge } from '@/components/ui/badge';
import { Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getSplitPartLabelData,
  type SplitGroupIdentity,
  type SplitGroupTicketShape,
  type SplitPartLabelData,
} from '@/utils/splitGroupIdentity';

interface SplitLinkProps {
  splitParentId?: string | null;
  splitGroupId?: string | null;
  splitSequence?: number | null;
  /**
   * When set, the ticket is a rollover / EOD continuation. The badge
   * hides itself to avoid duplicating the separate RolloverBadge
   * already rendered by the caller.
   */
  rolloverOfTicketId?: string | null;
  /**
   * Optional reconstructed group identity. When supplied alongside a
   * resolvable ticket shape (via the explicit props or `ticket`), the
   * badge renders the computed "Part X of Y" label via
   * `getSplitPartLabelData`.
   */
  splitGroup?: SplitGroupIdentity | null;
  /**
   * Optional pre-formatted label data. Takes precedence over
   * `splitGroup` for callers that have already computed the label.
   */
  splitLabelData?: SplitPartLabelData | null;
  /**
   * Optional explicit ticket shape for label computation. Defaults to
   * a shape built from the other props (id is unused in label math —
   * lookup uses `splitGroupId` + `splitSequence`).
   */
  ticket?: SplitGroupTicketShape | null;
  className?: string;
}

export function SplitLink({
  splitParentId,
  splitGroupId,
  splitSequence,
  rolloverOfTicketId,
  splitGroup,
  splitLabelData,
  ticket,
  className,
}: SplitLinkProps) {
  // Phase 7.2: rollover continuations are conveyed by the separate
  // RolloverBadge — hide the split badge for them to avoid dual labels.
  if (rolloverOfTicketId) return null;

  // Phase 7.2: nothing to render without a group id.
  if (!splitGroupId) return null;

  // Phase 7.2: resolve the displayed label in priority order:
  //   1. `splitLabelData.label` (caller pre-computed via helper)
  //   2. `getSplitPartLabelData(ticket, splitGroup)` (caller supplied group)
  //   3. Safe fallback: "Split" — NEVER "of 2".
  let label: string | null = null;
  if (splitLabelData?.label) {
    label = splitLabelData.label;
  } else if (splitGroup) {
    const t: SplitGroupTicketShape =
      ticket ?? {
        id: (ticket as any)?.id ?? splitParentId ?? splitGroupId,
        split_parent_id: splitParentId,
        split_group_id: splitGroupId,
        split_sequence: splitSequence,
      };
    const data = getSplitPartLabelData(t, splitGroup);
    label = data.label;
  }

  // Generic fallback when no group identity is supplied. Phase 7.2
  // explicitly avoids fabricating a part total — callers without group
  // context see a non-binary safe label.
  const displayLabel = label ?? 'Split';

  return (
    <Badge
      variant="outline"
      className={cn(
        'text-xs flex items-center gap-1 bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20',
        className,
      )}
      title={
        splitParentId
          ? `Original ticket: ${splitParentId}`
          : `Split group: ${splitGroupId}`
      }
    >
      <Link2 className="h-3 w-3" />
      {displayLabel}
    </Badge>
  );
}
